/**
 * テロップのフォント + ウェイト選択。Premiere のフォント一覧のように、各フォント名の横に
 * そのフォントで描画したサンプル (「生成せいセイ」) を出し、フォント行を展開すると
 * そのフォントが提供する数値ウェイトを子行として (実際の太さで) 並べる。
 *
 * 書体数が多い (CAPTION_FONT_FAMILIES) ので、分類 (ゴシック / 明朝 / 丸ゴシック / 手書き /
 * デザイン / 欧文) ごとに見出しを挟む。許可リストは分類ごとにまとまった順に並べてあるので、
 * 直前の書体と分類が変わったところで見出しを出すだけでよい。
 *
 * メニューは align="right" でアンカーの右端に揃えて左へ広げる。フォント選択は右ドックの中に
 * あり、右へ広げると画面端に当たって位置が不自然になるため。
 *
 * ブラウザ標準の <select> は option ごとにフォントを変えられないため独自のドロップダウンにする。
 * サンプル描画に使うフォントは、一覧を開いたときにレンダラーと同じ loadCaptionFont
 * (@remotion/google-fonts) で読み込む (unicode-range 分割配信のため、サンプル文字に必要な
 * ファイルだけが取得される)。ウェイトの子行は展開したフォントの分だけ読み込む。
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Check } from 'lucide-react';
import {
  CAPTION_FONT_AVAILABLE_WEIGHTS,
  CAPTION_FONT_CATEGORIES,
  CAPTION_FONT_DESCRIPTIONS,
  CAPTION_FONT_FAMILIES,
  resolveCaptionFontFamily,
  resolveCaptionFontWeight,
  type CaptionFontFamilyName,
  type CaptionFontWeightInput,
} from '../fonts/captionFonts';
import { loadCaptionFont } from '../remotion/captionFontLoader';
import { Popover } from './Popover';
import styles from './FontPicker.module.css';

const SAMPLE_TEXT = '生成せいセイ';

interface FontPickerProps {
  family: string;
  familyOnly?: boolean;
  selectedFamilies?: readonly string[];
  disabled?: boolean;
  weight: CaptionFontWeightInput;
  onChange: (next: { fontFamily: CaptionFontFamilyName; fontWeight: number }) => void;
}

/** サンプル文字をそのフォント・ウェイトで描画する (描画時にフォントを読み込む)。 */
function FontSample({ family, weight }: { family: CaptionFontFamilyName; weight: number }) {
  const font = loadCaptionFont(family, weight);
  return (
    <span
      className={styles.sample}
      style={{ fontFamily: font.fontFamily, fontWeight: font.fontWeight, fontFeatureSettings: '"palt"' }}
    >
      {SAMPLE_TEXT}
    </span>
  );
}

export function FontPicker({ family, weight, onChange, familyOnly = false, disabled = false, selectedFamilies }: FontPickerProps) {
  const currentFamily = resolveCaptionFontFamily(family);
  const currentWeight = resolveCaptionFontWeight(currentFamily, weight);
  const [open, setOpen] = useState(false);
  // 展開中のフォント (ウェイト子行を出す)。開いた直後は現在のフォントを展開しておく。
  const [expanded, setExpanded] = useState<Set<CaptionFontFamilyName>>(() => new Set());
  const rootRef = useRef<HTMLDivElement>(null);
  // 選択中のフォントの行。開いたときに一覧をここが中央に来るまでスクロールする
  const currentRowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setExpanded(new Set([currentFamily]));
    // 開いた瞬間のフォントだけ展開すればよい (選択で currentFamily が変わっても畳まない)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // 一覧 (role=listbox のスクロール領域) の中で選択中の行が上下中央に来る位置まで送る。
    // scrollIntoView は画面 (window) まで動かしてしまうので、一覧の scrollTop だけを変える。
    // Popover の位置決めの後に測るため 1 フレーム待つ
    const frame = requestAnimationFrame(() => {
      const row = currentRowRef.current;
      const menu = row?.closest('[role="listbox"]');
      if (!row || !(menu instanceof HTMLElement)) return;
      const menuRect = menu.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      menu.scrollTop += rowRect.top - menuRect.top - (menu.clientHeight - rowRect.height) / 2;
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const toggleExpanded = (name: CaptionFontFamilyName) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const select = (fontFamily: CaptionFontFamilyName, fontWeight: number) => {
    onChange({ fontFamily, fontWeight });
    if (!selectedFamilies) setOpen(false);
  };

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open && !disabled}
        title={familyOnly && !family ? 'フォントを選択' : CAPTION_FONT_DESCRIPTIONS[currentFamily]}
      >
        <span className={styles.triggerName}>{selectedFamilies ? 'フォントを選択' : familyOnly && !family ? '未設定' : currentFamily}</span>
        {!familyOnly && <span className={styles.triggerWeight}>{currentWeight}</span>}
        <ChevronDown size={14} className={styles.triggerChevron} />
      </button>

      {/* 一覧は body へポータルし、画面内に収まる位置に fixed で出す (Popover 参照) */}
      <Popover
        anchorRef={rootRef}
        open={open && !disabled}
        onClose={() => setOpen(false)}
        className={styles.menu}
        align="right"
        role="listbox"
        ariaLabel="フォント"
        ariaMultiselectable={selectedFamilies ? true : undefined}
      >
          {CAPTION_FONT_FAMILIES.map((name, index) => {
            const weights = CAPTION_FONT_AVAILABLE_WEIGHTS[name];
            const isCurrent = selectedFamilies ? selectedFamilies.includes(name) : name === currentFamily && (!familyOnly || !!family);
            const isExpanded = expanded.has(name);
            const canExpand = !familyOnly && weights.length > 1;
            // フォント行を選んだときは、今のウェイトに最も近いウェイトを引き継ぐ
            const weightForFamily = resolveCaptionFontWeight(name, currentWeight);
            // 許可リストは分類ごとにまとまっているので、変わり目でだけ見出しを出す
            const category = CAPTION_FONT_CATEGORIES[name];
            const isCategoryStart =
              index === 0 || CAPTION_FONT_CATEGORIES[CAPTION_FONT_FAMILIES[index - 1]!] !== category;
            return (
              <Fragment key={name}>
                {isCategoryStart && <div className={styles.categoryLabel}>{category}</div>}
                <div className={styles.group}>
                  <div className={`${styles.row} ${isCurrent ? styles.rowCurrent : ''}`} ref={isCurrent && !selectedFamilies ? currentRowRef : undefined}>
                    <button
                      type="button"
                      className={styles.expandButton}
                      onClick={() => toggleExpanded(name)}
                      disabled={!canExpand}
                      aria-label={isExpanded ? 'ウェイトを閉じる' : 'ウェイトを表示'}
                      tabIndex={-1}
                    >
                      {canExpand ? (
                        isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />
                      ) : null}
                    </button>
                    <button
                      type="button"
                      className={styles.rowMain}
                      role="option"
                      aria-selected={isCurrent}
                      onClick={() => select(name, weightForFamily)}
                      title={CAPTION_FONT_DESCRIPTIONS[name]}
                    >
                      <span className={styles.rowName}>{selectedFamilies && isCurrent && <Check size={12} aria-hidden="true" />} {name}</span>
                      <FontSample family={name} weight={weightForFamily} />
                    </button>
                  </div>
                  {!familyOnly && isExpanded &&
                    weights.map((w) => {
                      const selected = isCurrent && w === currentWeight;
                      return (
                        <button
                          key={w}
                          type="button"
                          className={`${styles.row} ${styles.weightRow} ${selected ? styles.rowCurrent : ''}`}
                          role="option"
                          aria-selected={selected}
                          onClick={() => select(name, w)}
                        >
                          <span className={styles.weightCheck}>{selected ? <Check size={12} /> : null}</span>
                          <span className={styles.rowName}>{w}</span>
                          <FontSample family={name} weight={w} />
                        </button>
                      );
                    })}
                </div>
              </Fragment>
            );
          })}
      </Popover>
    </div>
  );
}
